import React, { PureComponent } from 'react';
import {
  Form,
  Field,
  reduxForm,
} from 'redux-form';
import PropTypes from 'prop-types';
import {
  Row, Col, Card, CardBody, Table, Badge,
} from 'reactstrap';

import CalendarBlankIcon from 'mdi-react/CalendarBlankIcon';

import TextBox from './form/TextBox';
import renderCheckBoxField from './form/CheckBox';
import Pagination from './pagination/Pagination';
import DatePicker from './form/DatePicker';
import renderSelectField from './form/Select';

// Funci�n helper para extraer texto de un elemento React o devolver el string directamente
const extractTextFromElement = (element) => {
  if (typeof element === 'string' || typeof element === 'number') {
    return String(element);
  }

  if (React.isValidElement(element)) {
    const { children } = element.props;
    if (typeof children === 'string' || typeof children === 'number') {
      return String(children);
    }
    if (Array.isArray(children)) {
      return children.map(child => extractTextFromElement(child)).join('');
    }
    if (React.isValidElement(children)) {
      return extractTextFromElement(children);
    }
  }

  return '';
};

const RenderDataTableAction = (actions, row) => {
  if (!(actions && actions.length)) {
    return null;
  }

  return (
    <span>
      {actions.map(a => (
        <span key={`action_${row.id}_${a.key}`}>
          <button
            onClick={() => a.callback && a.callback(row)}
            type="button"
            className="action-button"
            tabIndex="-1"
          >
            {typeof a.icon === 'function' ? a.icon(row) : a.icon}
          </button>
        </span>
      ))}
    </span>
  );
};

const RenderDataTableIcon = (col, row, key) => {
  if (!col.icon) {
    return null;
  }

  return (
    <span key={`icon_${key}`}>
      <button
        onClick={() => col.icon.callback && col.icon.callback(row)}
        type="button"
        className="action-button"
        tabIndex="-1"
      >
        {col.icon.component(row)}
      </button>
    </span>
  );
};

const RenderDataTableInput = (column, row, onAutoSave) => {
  if (!column.editable) {
    return null;
  }

  const attributes = {
    className: 'form-control',
  };

  if (column.type === 'number') {
    attributes.className = `${attributes.className}`;

    if (!Number.isNaN(+column.max)) {
      attributes.max = +column.max;
    }
    if (!Number.isNaN(+column.min)) {
      attributes.min = +column.min;
    }
  }

  if (column.type === 'select') {
    attributes.className = `${attributes.className}`;
    return (
      <Field
        key={`select_${row.id}_${column.key}`}
        name={`select_${row.id}_${column.key}`}
        component={renderSelectField}
        inputStyle={column.inputStyle && column.inputStyle(row, column.key)}
        type={column.type}
        options={column.searchProps.options || []}
        isMulti={column.searchProps.isMulti || false}
        isSearchable="false"
        onChange={(item) => {
          onAutoSave(row, column.key, item.value);
        }}
      />
    );
  }

  return (
    <span>
      <input
        {...attributes}
        key={`input_${row.id}_${column.key}`}
        name={`input_${row.id}_${column.key}`}
        defaultValue={row[column.key]}
        type={column.type || 'text'}
        style={column.inputStyle && column.inputStyle(row, column.key)}
        onChange={(e) => {
          e.preventDefault();

          const { value } = e.currentTarget;
          if (column.type === 'number' && (+value < +column.min || +value > +column.max)) {
            e.preventDefault();
            e.currentTarget.value = +row[column.key];
            return;
          }
          e.currentTarget.changed = true;
        }}
        onKeyDown={(e) => {
          if (e.currentTarget.changed && e.which === 13) {
            onAutoSave(row, column.key, e.currentTarget.value);
            e.currentTarget.changed = false;
          }
        }}
        onBlur={(e) => {
          if (e.currentTarget.changed) {
            onAutoSave(row, column.key, e.currentTarget.value);
            e.currentTarget.changed = false;
          }
        }}
        onFocus={e => e.currentTarget.select()}
      />
    </span>
  );
};

const RenderSearchField = (props) => {
  const {
    key, name, type, searchProps = {}, handleChangeText, handleChangeSelect, placeholder,
  } = props;

  // Usar placeholder si est� disponible, sino extraer texto de name
  const placeholderText = placeholder || extractTextFromElement(name);

  if (type === 'date') {
    return (
      <>
        <Field
          name={`${key}Desde`}
          component={DatePicker}
          active={false}
          autoComplete="false"
          placeholder={`${placeholderText} Desde`}
          onBlur={({ target }) => handleChangeText({ target })}
        />
        <Field
          name="horaDesde"
          component={TextBox}
          type="time"
          autoComplete="false"
          onBlur={({ target }) => handleChangeText({ target })}
        />
        <div className="form__form-group-icon">
          <CalendarBlankIcon />
        </div>
        <Field
          name={`${key}Hasta`}
          component={DatePicker}
          active={false}
          autoComplete="false"
          placeholder={`${placeholderText} Hasta`}
          onBlur={({ target }) => handleChangeText({ target })}
        />

        <Field
          name="horaHasta"
          component={TextBox}
          type="time"
          autoComplete="false"
          onBlur={({ target }) => handleChangeText({ target })}
        />
        <div className="form__form-group-icon">
          <CalendarBlankIcon />
        </div>
      </>
    );
  }

  if (type === 'number') {
    return (
      <Field
        name={key}
        component={TextBox}
        type={type}
        min={props.min}
        max={props.max}
        placeholder={placeholderText}
        autoComplete="false"
        onBlur={({ target }) => handleChangeText({ target })}
      />
    );
  }

  if (type === 'select') {
    return (
      <Field
        name={key}
        component={renderSelectField}
        type={type}
        placeholder={placeholderText}
        autoComplete={false}
        options={searchProps.options || []}
        isMulti={searchProps.isMulti || false}
        isSearchable={false}
        isClearable={false}
        onChange={
          attrs => handleChangeSelect({ target: { name, value: attrs } })
        }
      />
    );
  }

  return (
    <Field
      name={key}
      component={TextBox}
      type={type}
      placeholder={placeholderText}
      autoComplete="false"
      maxLength="32"
      onBlur={({ target }) => handleChangeText({ target })}
    />
  );
};

const RenderSearchForm = (props) => {
  const {
    key, name, handleChangeText, handleChangeSelect,
  } = props;
  console.log({ key, name });
  return (
    <Col lg={4} md={6} lx={12}>
      <div key={`serach_form_${key}`} className="form__form-group">
        <span className="form__form-group-label">
          {typeof name === 'string' || typeof name === 'number' ? name : name}
        </span>
        <div className="form__form-group-field">
          {RenderSearchField({ ...props, handleChangeText, handleChangeSelect })}
        </div>
      </div>
    </Col>
  );
};

RenderDataTableInput.propTypes = {
  key: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  min: PropTypes.string,
  max: PropTypes.string,
  inputStyle: PropTypes.func,
};
RenderDataTableInput.defaultProps = {
  type: 'text',
  min: '',
  max: '',
  inputStyle: () => ({ fontSize: '1.6rem' }),
};
RenderSearchField.propTypes = {
  key: PropTypes.string.isRequired,
  name: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]).isRequired,
  type: PropTypes.string,
  min: PropTypes.string,
  max: PropTypes.string,
  placeholder: PropTypes.string,
  searchProps: PropTypes.shape({}),
  handleChangeText: PropTypes.func.isRequired,
  handleChangeSelect: PropTypes.func.isRequired,
};
RenderSearchField.defaultProps = {
  type: 'text',
  searchProps: {},
  min: '',
  max: '',
  placeholder: '',
};

RenderSearchForm.propTypes = {
  key: PropTypes.string.isRequired,
  name: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]).isRequired,
  handleChangeText: PropTypes.func.isRequired,
  handleChangeSelect: PropTypes.func.isRequired,
};

class DataTable extends PureComponent {
  constructor(props) {
    super(props);
    const { autoHideSearch } = props;
    this.state = {
      showSearch: false,
      autoHideSearch,
    };
  }

  toggleSearch = (e) => {
    e.preventDefault();
    this.setState(prevState => ({ showSearch: !prevState.showSearch }));
  }

  toggleAutoHide = (e) => {
    e.preventDefault();

    const { onAutoHideChange } = this.props;
    const { autoHideSearch } = this.state;

    this.setState((prevState) => {
      const newVal = !prevState.autoHideSearch;
      return { autoHideSearch: newVal };
    }, () => {
      if (onAutoHideChange) {
        onAutoHideChange(autoHideSearch);
      }
    });
  }

  visible = (col) => {
    if (typeof col.visible === 'undefined') return true;
    return col.visible;
  }

  handleChangeText = ({ target }) => {
    const { type, value: inputValue, name } = target;
    const value = type === 'number' ? Number(inputValue) : inputValue.trim();

    const {
      onFiltersLabelsApplied,
      filtersLabelsApplied,
    } = this.props;

    // Si el valor no existe y el filtro no es parte de filters, no hacemos cambios
    if (!value && !filtersLabelsApplied.some(f => f.name === name && f.value === value)) return;

    // Si el valor existe y el filtro no est� en filters, lo a�adimos
    if (value && !filtersLabelsApplied.some(f => f.name === name && f.value === value)) {
      onFiltersLabelsApplied([...filtersLabelsApplied, { name, value }]);
      return;
    }

    // Si el valor no existe y el filtro est� en filters, lo eliminamos
    if (!value && filtersLabelsApplied.some(f => f.name === name)) {
      onFiltersLabelsApplied(filtersLabelsApplied.filter(filter => filter.name !== name));
    }
  };

  handleChangeSelect = ({ target }) => {
    const { value: inputValue, name } = target;
    const value = Array.isArray(inputValue) && inputValue.length > 0
      ? inputValue.some(i => i.label)
      : inputValue && inputValue.label;

    const { onFiltersLabelsApplied, filtersLabelsApplied } = this.props;

    // Si el valor no existe y el filtro no est� en filters, no hacemos cambios
    if (!value && !filtersLabelsApplied.some(f => f.name === name)) return;

    // Si el valor existe y el filtro no est� en filters, lo a�adimos
    if (value && !filtersLabelsApplied.some(f => f.name === name)) {
      onFiltersLabelsApplied([...filtersLabelsApplied, { name, value }]);
      return;
    }

    // Si el valor existe y el filtro ya est� en filters, lo actualizamos
    if (value && filtersLabelsApplied.some(f => f.name === name)) {
      onFiltersLabelsApplied(filtersLabelsApplied.map(f => (f.name === name ? { ...f, value } : f)));
      return;
    }

    // Si el valor no existe y el filtro est� en filters, lo eliminamos
    if (!value && filtersLabelsApplied.some(f => f.name === name)) {
      onFiltersLabelsApplied(filtersLabelsApplied.filter(filter => filter.name !== name));
    }
  };

  render() {
    const {
      title,
      columns,
      items,
      getGridActions,
      onChangePage,
      handleSubmit,
      onClearFilters,
      filtersLabelsApplied,
      page,
      pageSize,
      totalItemsCount,
      onAutoSave,
    } = this.props;

    const {
      showSearch,
      submitting,
      autoHideSearch,
    } = this.state;

    const searchable = columns.filter(s => s.search === true).length > 0;
    const showForm = showSearch || !autoHideSearch;
    const searchColumns = columns
      .map((c, idx) => ({ ...c, origIndex: idx }))
      .filter(c => c.search === true)
      .sort((a, b) => {
        const oa = (typeof a.orderFilter === 'number')
          ? a.orderFilter
          : a.origIndex;
        const ob = (typeof b.orderFilter === 'number')
          ? b.orderFilter
          : b.origIndex;
        return oa - ob;
      });

    return (
      <Col md={12} lg={12}>
        <Card>
          <CardBody>
            {title || searchable ? (
              <div className="card__title">
                <Row>
                  {title ? (
                    <Col xs={12} md={9}>
                      <h5>{title}</h5>
                    </Col>
                  ) : null}
                  {searchable && (
                    <Col xs={12} md={3}>
                      <button
                        type="button"
                        className="table-search-toggle form__form-group-button"
                        onClick={e => this.toggleSearch(e)}
                      >
                        <span className="fa fa-search" /> B&uacute;squeda&nbsp;
                        <span className={`fa fa-chevron-${showForm ? 'up' : 'down'}`} />
                      </button>
                    </Col>
                  )}
                </Row>
              </div>
            ) : null}
            {searchable && false && (
              <Row>
                <Col xs={12}>
                  <div style={{
                    display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gap: '1rem',
                  }}
                  >
                    <h5>Filtros:</h5>
                    <div style={{
                      display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gap: '1rem',
                    }}
                    >
                      {filtersLabelsApplied.map(filter => (
                        <div>
                          <Badge
                            style={{ fontSize: '12px', paddingTop: '4px' }}
                            color="secondary"
                            pill
                          >
                            {filter.name}
                          </Badge>
                          <Badge
                            style={{ fontSize: '12px', paddingTop: '4px' }}
                            color="info"
                            pill
                          >
                            {filter.value}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
              </Row>
            )}
            {showForm ? (
              <>
                <Form className="form form--horizontal" onSubmit={handleSubmit}>
                  {
                    showForm && searchColumns.map(h => RenderSearchForm({
                      ...h,
                      handleChangeText: () => { }, // this.handleChangeText,
                      handleChangeSelect: () => { }, // this.handleChangeSelect,
                    }))
                  }
                  {showForm
                    && (
                      <div className="form__form-group" style={{ marginBottom: '0' }}>
                        <div className="form__form-group-field">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting}
                          ><span className="fa fa-search" /> Buscar
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary"
                            disabled={submitting}
                            onClick={onClearFilters}
                          ><span className="fa fa-broom" /> Limpiar filtros
                          </button>
                          <div>
                            <Field
                              name="autoHideSearch"
                              component={renderCheckBoxField}
                              label="Auto-ocultar buscador"
                              value={autoHideSearch ? 'checked' : ''}
                              defaultChecked={autoHideSearch}
                              onClick={e => this.toggleAutoHide(e)}
                            />
                          </div>
                        </div>
                      </div>
                    )
                  }
                </Form>
                <hr />
              </>
            ) : null}
            <Table responsive className="table--bordered">
              <thead>
                <tr>
                  {columns.map(h => this.visible(h) && (
                    <th key={`th_${h.key}`} width={h.width ? h.width : 'auto'} className={h.className || 'text-left'}>
                      {h.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map((u, uIndex) => (
                    <tr key={u.id || uIndex}>
                      {columns.map((h, hIndex) => this.visible(h) && (
                        <td key={`${u.id || uIndex}_${h.key}`} className={h.className || 'text-left'}>
                          {
                            !h.editable && (
                              <span>
                                {RenderDataTableIcon(h, u, `${u.id || uIndex}_${h.id || hIndex}`)}
                                {h.format ? h.format(u) : u[h.key]}
                              </span>
                            )
                          }
                          {RenderDataTableAction(getGridActions(h.key, u), u)}
                          {RenderDataTableInput(h, u, onAutoSave)}
                        </td>
                      ))}
                    </tr>
                  )))
                  : (
                    <tr>
                      <td>
                        <h5 className="subhead">No se encontraron resultados</h5>
                      </td>
                    </tr>
                  )
                }
              </tbody>
            </Table>
            <Pagination
              itemsCount={totalItemsCount}
              itemsToShow={pageSize}
              pageOfItems={page}
              onChangePage={onChangePage}
            />
          </CardBody>
        </Card>
      </Col>
    );
  }
}

DataTable.propTypes = {
  title: PropTypes.string,
  initialValues: PropTypes.shape({}).isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  items: PropTypes.arrayOf(PropTypes.shape([])).isRequired,
  getGridActions: PropTypes.func,
  handleSubmit: PropTypes.func,
  onClearFilters: PropTypes.func,
  onAutoHideChange: PropTypes.func,
  onFiltersLabelsApplied: PropTypes.func,
  filtersLabelsApplied: PropTypes.arrayOf(PropTypes.shape([])),
  onChangePage: PropTypes.func,
  onAutoSave: PropTypes.func,
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.number,
  totalItemsCount: PropTypes.number,
  autoHideSearch: PropTypes.bool,
};

DataTable.defaultProps = {
  title: '',
  pageSize: 25,
  getGridActions: () => [],
  handleSubmit: () => null,
  onClearFilters: () => null,
  onFiltersLabelsApplied: () => null,
  onChangePage: () => null,
  onAutoSave: () => null,
  onAutoHideChange: () => null,
  autoHideSearch: true,
  totalItemsCount: 0,
  filtersLabelsApplied: [],
};

const DataTableForm = reduxForm({
  form: 'SearchUserForm',
})(DataTable);

export default DataTableForm;
